import React, { useState, useEffect } from "react";
import "./TrackerPage.css"; 
import { useHistoryLogger } from "../../contexts/HistoryContext";

export default function TrackerTable({ trackerData, setTrackerData, products, setSelectedProduct, setActiveTab }) {
  console.log("🔍 TrackerTable Props:", { trackerData, setTrackerData, products });

  const { logHistory } = useHistoryLogger();

  // ✅ HELPER: Safely convert string or array to array
  const getSafeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') return value.split(", ").filter(v => v);
    return [];
  };

  const handleChange = async (idx, field, value) => {
    const oldData = [...trackerData];
    const row = oldData[idx];

    const oldValue = row[field];
    if (oldValue === value) return;

    const updated = [...trackerData];
    updated[idx] = { ...row, [field]: value };

    setTrackerData(updated);

    try {
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/tracker/update", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submission_id: row.submission_id,
          field,
          value
        })
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || "Update failed");
      }

    } catch (err) {
      console.error("Update tracker error:", err);
      setTrackerData(oldData);
      alert("Error updating tracker: " + err.message);
    }

    logHistory({
      antibodyId: row.submission_id,
      action: `${field} changed from "${oldValue}" to "${value}"`
    });
  };

  const handleGoToProduct = (antibodyId) => {
    if (!products || !setSelectedProduct || !setActiveTab) return;
    
    const product = products.find((p) => p.id === antibodyId);
    if (product) {
      setSelectedProduct(product);
      setActiveTab("home");
    }
  };

  const multiSelectOptions = {
    proposedMaterial: ["Hybridoma Cell Lines", "Polyclonal Antisera", "Purified Antibodies", "Supernatant", "Plasmids"],
    statusWeeks: ["Growing", "Not Growing", "Contaminated"],
    status5Weeks: ["Growing", "Not Growing", "Contaminated"],
    marketingPlan: ["Twitter", "Bluesky", "Blog", "Email"]
  };

  const handleMultiSelectChange = async (idx, field, option) => {
    const updated = [...trackerData];
    const row = updated[idx];

    const current = getSafeArray(row[field]);

    let newValue;

    if (current.includes(option)) {
      newValue = current.filter(o => o !== option);
    } else {
      newValue = [...current, option];
    }

    row[field] = newValue;
    
    setTrackerData(updated);

    try {
      await fetch("https://dshb-deposits.its.uiowa.edu/api/tracker/update", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submission_id: row.submission_id,
          field,
          value: newValue.join(", ")
        })
      });

      logHistory({
        antibodyId: row.submission_id,
        action: `${field} updated to: ${newValue.join(", ")}`
      });
    } catch (err) {
      console.error("Update error:", err);
      alert("Error updating: " + err.message);
    }
  };

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return "";
    if (typeof dateValue === 'string') {
      return dateValue.split('T')[0];
    }
    if (dateValue instanceof Date) {
      return dateValue.toISOString().split('T')[0];
    }
    return "";
  };

  return (
    <div className="tracker-page">
      <h2>Depositor Tracker</h2>
      <div className="tracker-table-wrapper">
        <table className="tracker-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Description</th>
              <th>Depositor Name</th>
              <th>Depositor Email</th>
              <th>Submitted By</th>
              <th>Curation Notes</th>
              <th>Proposed Material</th>
              <th>Material Details</th>
              <th>Status</th>
              <th>Initial Contact Date</th>
              <th>Info Template Sent</th>
              <th>Agreement Sent</th>
              <th>Agreement Signed</th>
              <th>Antibody Registry ID</th>
              <th>NetSuite Uploaded</th>
              <th>Shipped by Depositor</th>
              <th>Tracking ID</th>
              <th>Date Received</th>
              <th>Growth Start Date</th>
              <th>Status at Weeks</th>
              <th>Status at 5 Weeks</th>
              <th>On Shelf</th>
              <th>Validation Ref</th>
              <th>Imaging Reference</th>
              <th>Marketing Plan</th>
              <th>Original Message</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {trackerData && trackerData.length > 0 ? (
              trackerData.map((row, idx) => (
                <tr key={idx}>
                  <td
                    className="clickable"
                    onClick={() => handleGoToProduct(row.submission_id)}
                    title="Go to product details"
                  >
                    {row.submission_id}
                  </td>
                  <td
                    className="editable-cell clickable"
                    onClick={() => handleGoToProduct(row.submission_id)}
                  >
                    {row.description || row.item_name_number}
                  </td>
                  <td>{row.depositor_name_contributor || "-"}</td>
                  <td>{row.depositor_email || "-"}</td>
                  <td
                    className="editable-cell"
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => handleChange(idx, "submittedBy", e.currentTarget.textContent)}
                  >
                    {row.submittedBy || "-"}
                  </td>
                  <td
                    className="editable-cell"
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => handleChange(idx, "curationNotes", e.currentTarget.textContent)}
                  >
                    {row.curationNotes || "-"}
                  </td>
                  {/* ✅ PROPOSED MATERIAL - Using getSafeArray */}
                  <td>
                    {multiSelectOptions.proposedMaterial.map((opt) => (
                      <label key={opt}>
                        <input
                          type="checkbox"
                          checked={getSafeArray(row.proposedMaterial).includes(opt)}
                          onChange={() => handleMultiSelectChange(idx, "proposedMaterial", opt)}
                        />
                        {opt}
                      </label>
                    ))}
                  </td>
                  <td
                    className="editable-cell"
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => handleChange(idx, "materialDetails", e.currentTarget.textContent)}
                  >
                    {row.materialDetails || "-"}
                  </td>
                  <td>
                    <select
                      value={row.status || ""}
                      onChange={(e) => handleChange(idx, "status", e.target.value)}
                    >
                      <option value="">--Select--</option>
                      <option value="Approved">Approved</option>
                      <option value="Pending">Pending</option>
                      <option value="Denied">Denied</option>
                    </select>
                  </td>
                  {/* ✅ FIXED: initialContactDate field */}
                  <td>
                    <input
                      type="date"
                      value={formatDateForInput(row.initialContactDate)}
                      onChange={(e) => handleChange(idx, "initialContactDate", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.infoTemplateSent == 1 || row.infoTemplateSent === true || false}
                      onChange={(e) => handleChange(idx, "infoTemplateSent", e.target.checked ? 1 : 0)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.agreementSent == 1 || row.agreementSent === true || false}
                      onChange={(e) => handleChange(idx, "agreementSent", e.target.checked ? 1 : 0)}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={formatDateForInput(row.agreementSigned)}
                      onChange={(e) => handleChange(idx, "agreementSigned", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.antibodyRegistryIdObtained == 1 || row.antibodyRegistryIdObtained === true || false}
                      onChange={(e) => handleChange(idx, "antibodyRegistryIdObtained", e.target.checked ? 1 : 0)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.netSuiteUploaded == 1 || row.netSuiteUploaded === true || false}
                      onChange={(e) => handleChange(idx, "netSuiteUploaded", e.target.checked ? 1 : 0)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.shippedByDepositor == 1 || row.shippedByDepositor === true || false}
                      onChange={(e) => handleChange(idx, "shippedByDepositor", e.target.checked ? 1 : 0)}
                    />
                  </td>
                  <td
                    className="editable-cell"
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => handleChange(idx, "trackingId", e.currentTarget.textContent)}
                  >
                    {row.trackingId || "-"}
                  </td>
                  {/* ✅ FIXED: dateReceivedByLab field */}
                  <td>
                    <input
                      type="date"
                      value={formatDateForInput(row.dateReceivedByLab)}
                      onChange={(e) => handleChange(idx, "dateReceivedByLab", e.target.value)}
                    />
                  </td>
                  {/* ✅ FIXED: cellGrowthStartDate field */}
                  <td>
                    <input
                      type="date"
                      value={formatDateForInput(row.cellGrowthStartDate)}
                      onChange={(e) => handleChange(idx, "cellGrowthStartDate", e.target.value)}
                    />
                  </td>
                  {/* ✅ STATUS WEEKS - Using getSafeArray */}
                  <td>
                    {multiSelectOptions.statusWeeks.map((opt) => (
                      <label key={opt}>
                        <input
                          type="checkbox"
                          checked={getSafeArray(row.statusWeeks).includes(opt)}
                          onChange={() => handleMultiSelectChange(idx, "statusWeeks", opt)}
                        />
                        {opt}
                      </label>
                    ))}
                  </td>
                  {/* ✅ STATUS 5 WEEKS - Using getSafeArray */}
                  <td>
                    {multiSelectOptions.status5Weeks.map((opt) => (
                      <label key={opt}>
                        <input
                          type="checkbox"
                          checked={getSafeArray(row.status5Weeks).includes(opt)}
                          onChange={() => handleMultiSelectChange(idx, "status5Weeks", opt)}
                        />
                        {opt}
                      </label>
                    ))}
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.onShelf == 1 || row.onShelf === true || false}
                      onChange={(e) => handleChange(idx, "onShelf", e.target.checked ? 1 : 0)}
                    />
                  </td>
                  <td
                    className="editable-cell"
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => handleChange(idx, "validationRef", e.currentTarget.textContent)}
                  >
                    {row.validationRef || "-"}
                  </td>
                  <td
                    className="editable-cell"
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => handleChange(idx, "imagingReference", e.currentTarget.textContent)}
                  >
                    {row.imagingReference || "-"}
                  </td>
                  {/* ✅ MARKETING PLAN - Using getSafeArray */}
                  <td>
                    {multiSelectOptions.marketingPlan.map((opt) => (
                      <label key={opt}>
                        <input
                          type="checkbox"
                          checked={getSafeArray(row.marketingPlan).includes(opt)}
                          onChange={() => handleMultiSelectChange(idx, "marketingPlan", opt)}
                        />
                        {opt}
                      </label>
                    ))}
                  </td>
                  <td
                    className="editable-cell"
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => handleChange(idx, "originalRequestMessage", e.currentTarget.textContent)}
                  >
                    {row.originalRequestMessage || "-"}
                  </td>
                  <td>{row.updated_at ? new Date(row.updated_at).toLocaleDateString() : "-"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="28" style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>
                  No tracker data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
